import { toEntityPayload } from "@/components/alerts/add-v2/model/adapters";
import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";
import { BULLETIN_TYPE } from "@/components/alerts/constants";
import { BulletinResourceResponse } from "@/types";

/**
 * `BulletinFormValues` → the resource `BulletinCard` reads.
 *
 * Card view reuses the production card for the same reason full view reuses `DisplayAlert`
 * (decision 19): the board is what the officer is writing *for*, and a second implementation of it
 * would drift from the real one. The card takes a whole `BulletinResourceResponse`, so this is the
 * other direction of `adapters.ts` — but only as far as the dozen fields the card actually reads.
 *
 * The rest is **cast, not invented**. A resource has a `creator`, a `time_created`, a `location` and
 * a `tags` blob; a form that has never been saved has none of them, and filling them with plausible
 * values would put data on screen that no bulletin will ever hold. The cast is the honest version of
 * "the card reads twelve fields".
 *
 * `translated_type` is passed in rather than derived, because the name behind a `reference_name`
 * lives in an option list and this module stays pure (decision 5).
 */
export const toBulletinCardResource = (
  values: BulletinFormValues,
  translatedType: string,
): BulletinResourceResponse =>
  ({
    // Nothing reads it but the share button, which the preview does not let anyone press.
    id: 0,
    type: values.bulletinType as BULLETIN_TYPE,
    translated_type: translatedType,
    // The form has no priority field; the card falls back to its default accent.
    priority: null,
    associated_crime: values.associatedCrimes.map((crime) => crime.crime),
    occurrence_number: values.occurrenceNumbers,
    operational_area: values.operationalArea,
    issuing_police_service: values.policeService
      ? {
          id: values.policeService.id,
          name: values.policeService.name,
          is_police_service: true,
          is_self_group: false,
        }
      : undefined,
    issued_time: values.issuedTime?.toISOString() ?? "",
    description: values.description,

    // Keyed by filename, as the bulletin row stores them. The card shows images and videos only,
    // and thumbnails everything else away, so a file with no derivative falls back to itself.
    evidence_files: Object.fromEntries(
      values.media.map((item) => [
        item.filename,
        {
          filename: item.filename,
          content_type: item.contentType,
          download_url: item.url,
          thumbnail_url: item.thumbnailUrl ?? item.url,
          sort_ordinal: item.sortOrdinal,
          // The card captions its hero the same way the detail view does, off this relation.
          entity_ids: item.entityIds,
          openai_description: {},
        },
      ]),
    ),

    // Read only for that caption -- the card shows no entity list of its own.
    entities: values.entities.map(toEntityPayload),

    // Both are read unguarded by the card. Empty is also the truth: a bulletin nobody has published
    // has no comments and no flags.
    resolutions: [],
    flags: [],
  }) as unknown as BulletinResourceResponse;
